import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  FiBookOpen,
  FiPlus,
  FiSearch,
  FiUser,
  FiPhone,
  FiArrowUpRight,
  FiArrowDownLeft,
  FiTrash2,
  FiEdit2,
  FiRefreshCw,
  FiX,
  FiDollarSign,
  FiUserPlus,
} from "react-icons/fi";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import ToolsHeader from "./ToolsHeader";
import {
  getAllKhatabooks,
  createKhatabook,
  getKhatabookSummary,
  listKhatabookCustomers,
  addKhatabookCustomer,
  editKhatabookCustomer,
  deleteKhatabookCustomer,
  getCustomerSummary,
  searchCustomerTransactions,
  addCustomerTransaction,
  editCustomerTransaction,
  deleteCustomerTransaction,
} from "../../api/tools/toolsApi";
import "./Tools.css";

const KhatabookTool = () => {
  const authState = useSelector((state) => state.auth);
  const loggedInUser = authState?.user ?? authState;
  const token =
    loggedInUser?.refreshToken ||
    authState?.refreshToken ||
    loggedInUser?.token ||
    authState?.token;

  // Khatabooks list state
  const [khatabooks, setKhatabooks] = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);
  const [bookSummary, setBookSummary] = useState(null);
  const [loadingBooks, setLoadingBooks] = useState(true);

  // Customers state
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");

  // Customer transactions / drawer state
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSummary, setCustomerSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  // Modals
  const [showCreateBookModal, setShowCreateBookModal] = useState(false);
  const [newBookName, setNewBookName] = useState("");
  const [submittingBook, setSubmittingBook] = useState(false);

  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerForm, setCustomerForm] = useState({ id: null, name: "", mobile_number: "" });
  const [submittingCustomer, setSubmittingCustomer] = useState(false);

  const [showTxModal, setShowTxModal] = useState(false);
  const [txForm, setTxForm] = useState({
    id: null,
    amount: "",
    transaction_type: "GAVE",
    description: "",
  });
  const [submittingTx, setSubmittingTx] = useState(false);

  // 1. Fetch All Khatabooks
  const fetchKhatabooks = useCallback(async () => {
    if (!token) return;
    setLoadingBooks(true);
    try {
      const data = await getAllKhatabooks(token);
      const list =
        data?.khatabooks ||
        data?.data ||
        (Array.isArray(data) ? data : []);
      setKhatabooks(list);

      if (list.length > 0 && !selectedBook) {
        setSelectedBook(list[0]);
      } else if (list.length === 0) {
        setSelectedBook(null);
      }
    } catch (err) {
      console.error("Error fetching khatabooks:", err);
      toast.error(err?.response?.data?.message || "Failed to load Khatabooks");
    } finally {
      setLoadingBooks(false);
    }
  }, [token, selectedBook]);

  useEffect(() => {
    fetchKhatabooks();
  }, [fetchKhatabooks]);

  // 2. Fetch Book Summary & Customers when selectedBook changes
  const fetchBookDetails = useCallback(async (bookId) => {
    if (!token || !bookId) return;
    setLoadingCustomers(true);
    try {
      const [summaryRes, customersRes] = await Promise.allSettled([
        getKhatabookSummary(token, bookId),
        listKhatabookCustomers(token, bookId, 1, 1000),
      ]);

      if (summaryRes.status === "fulfilled") {
        setBookSummary(summaryRes.value?.data || summaryRes.value?.summary || summaryRes.value || null);
      } else {
        setBookSummary(null);
      }

      if (customersRes.status === "fulfilled") {
        const custList =
          customersRes.value?.customers ||
          customersRes.value?.data ||
          (Array.isArray(customersRes.value) ? customersRes.value : []);
        setCustomers(custList);
      } else {
        setCustomers([]);
      }
    } catch (err) {
      console.error("Error fetching book details:", err);
    } finally {
      setLoadingCustomers(false);
    }
  }, [token]);

  useEffect(() => {
    if (selectedBook?.id) {
      fetchBookDetails(selectedBook.id);
    }
  }, [selectedBook, fetchBookDetails]);

  // 3. Create Khatabook
  const handleCreateBook = async (e) => {
    e.preventDefault();
    if (!newBookName.trim()) {
      toast.warning("Please enter a Khatabook name");
      return;
    }
    setSubmittingBook(true);
    try {
      const res = await createKhatabook(token, { name: newBookName.trim() });
      toast.success("Khatabook created successfully!");
      setShowCreateBookModal(false);
      setNewBookName("");
      const created = res?.khatabook || res?.data || res;
      await fetchKhatabooks();
      if (created?.id) setSelectedBook(created);
    } catch (err) {
      console.error("Error creating khatabook:", err);
      toast.error(err?.response?.data?.message || "Failed to create Khatabook");
    } finally {
      setSubmittingBook(false);
    }
  };

  // 4. Add or Edit Customer
  const handleSaveCustomer = async (e) => {
    e.preventDefault();
    if (!selectedBook?.id) {
      toast.warning("Please select a Khatabook first");
      return;
    }
    if (!customerForm.name.trim() || !customerForm.mobile_number.trim()) {
      toast.warning("Please fill in both name and mobile number");
      return;
    }
    setSubmittingCustomer(true);
    try {
      if (customerForm.id) {
        await editKhatabookCustomer(token, customerForm.id, {
          name: customerForm.name,
          mobile_number: customerForm.mobile_number,
        });
        toast.success("Customer updated successfully");
      } else {
        await addKhatabookCustomer(token, selectedBook.id, {
          name: customerForm.name,
          mobile_number: customerForm.mobile_number,
        });
        toast.success("Customer added successfully");
      }
      setShowCustomerModal(false);
      setCustomerForm({ id: null, name: "", mobile_number: "" });
      fetchBookDetails(selectedBook.id);
    } catch (err) {
      console.error("Error saving customer:", err);
      toast.error(err?.response?.data?.message || "Failed to save customer");
    } finally {
      setSubmittingCustomer(false);
    }
  };

  // 5. Delete Customer
  const handleDeleteCustomer = async (customerId, e) => {
    e?.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this customer?")) return;
    try {
      await deleteKhatabookCustomer(token, customerId);
      toast.success("Customer deleted");
      if (selectedCustomer?.id === customerId) {
        setSelectedCustomer(null);
      }
      fetchBookDetails(selectedBook.id);
    } catch (err) {
      console.error("Error deleting customer:", err);
      toast.error(err?.response?.data?.message || "Failed to delete customer");
    }
  };

  // 6. Open Customer Details & Transactions
  const handleOpenCustomer = useCallback(async (cust) => {
    setSelectedCustomer(cust);
    setLoadingTransactions(true);
    try {
      const [sumRes, txRes] = await Promise.allSettled([
        getCustomerSummary(token, cust.id),
        searchCustomerTransactions(token, cust.id, 1, 100),
      ]);

      if (sumRes.status === "fulfilled") {
        setCustomerSummary(sumRes.value?.data || sumRes.value?.summary || sumRes.value || null);
      } else {
        setCustomerSummary(null);
      }

      if (txRes.status === "fulfilled") {
        const list =
          txRes.value?.transactions ||
          txRes.value?.data ||
          (Array.isArray(txRes.value) ? txRes.value : []);
        setTransactions(list);
      } else {
        setTransactions([]);
      }
    } catch (err) {
      console.error("Error loading customer transactions:", err);
    } finally {
      setLoadingTransactions(false);
    }
  }, [token]);

  // 7. Add or Edit Transaction
  const handleSaveTransaction = async (e) => {
    e.preventDefault();
    if (!selectedCustomer?.id) return;
    if (!txForm.amount || Number(txForm.amount) <= 0) {
      toast.warning("Please enter a valid amount");
      return;
    }
    setSubmittingTx(true);
    try {
      if (txForm.id) {
        await editCustomerTransaction(token, txForm.id, {
          amount: Number(txForm.amount),
          transaction_type: txForm.transaction_type,
          description: txForm.description,
        });
        toast.success("Transaction updated");
      } else {
        await addCustomerTransaction(token, selectedCustomer.id, {
          amount: Number(txForm.amount),
          transaction_type: txForm.transaction_type,
          description: txForm.description,
        });
        toast.success("Transaction recorded");
      }
      setShowTxModal(false);
      setTxForm({ id: null, amount: "", transaction_type: "GAVE", description: "" });
      handleOpenCustomer(selectedCustomer);
      if (selectedBook?.id) fetchBookDetails(selectedBook.id);
    } catch (err) {
      console.error("Error saving transaction:", err);
      toast.error(err?.response?.data?.message || "Failed to save transaction");
    } finally {
      setSubmittingTx(false);
    }
  };

  // 8. Delete Transaction
  const handleDeleteTransaction = async (txId) => {
    if (!window.confirm("Are you sure you want to delete this transaction?")) return;
    try {
      await deleteCustomerTransaction(token, txId);
      toast.success("Transaction deleted");
      handleOpenCustomer(selectedCustomer);
      if (selectedBook?.id) fetchBookDetails(selectedBook.id);
    } catch (err) {
      console.error("Error deleting transaction:", err);
      toast.error(err?.response?.data?.message || "Failed to delete transaction");
    }
  };

  // Filtered customers
  const filteredCustomers = useMemo(() => {
    if (!customerSearch.trim()) return customers;
    const q = customerSearch.toLowerCase();
    return customers.filter(
      (c) =>
        (c.name && c.name.toLowerCase().includes(q)) ||
        (c.mobile_number && c.mobile_number.includes(q))
    );
  }, [customers, customerSearch]);

  // Compute stats
  const totalYouWillGive = bookSummary?.total_give || bookSummary?.totalGive || bookSummary?.you_give || 0;
  const totalYouWillGet = bookSummary?.total_get || bookSummary?.totalGet || bookSummary?.you_get || 0;
  const netBalance = totalYouWillGet - totalYouWillGive;

  return (
    <div className="tools-container">
      <ToolsHeader
        title="Khatabook Tool"
        subtitle="Manage customer credit/debit accounts, track who owes you, and record transactions."
        actions={
          <div className="d-flex align-items-center gap-2">
            <button
              className="tools-btn tools-btn--secondary"
              onClick={fetchKhatabooks}
              disabled={loadingBooks}
            >
              <FiRefreshCw className={loadingBooks ? "spin" : ""} /> Refresh
            </button>
            <button
              className="tools-btn tools-btn--primary"
              onClick={() => setShowCreateBookModal(true)}
            >
              <FiPlus /> New Khatabook
            </button>
          </div>
        }
      />

      {/* Khatabook Selector Bar */}
      <div className="tools-action-bar">
        <div className="d-flex align-items-center gap-3 flex-wrap flex-grow-1">
          <div className="d-flex align-items-center gap-2">
            <FiBookOpen className="text-primary" size={20} />
            <span className="fw-bold text-dark">Active Book:</span>
          </div>
          {loadingBooks ? (
            <span className="text-muted small">Loading books...</span>
          ) : khatabooks.length === 0 ? (
            <span className="text-muted small">No Khatabooks found. Create your first one!</span>
          ) : (
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {khatabooks.map((book) => (
                <button
                  key={book.id}
                  onClick={() => setSelectedBook(book)}
                  className={`tools-btn tools-btn--sm ${
                    selectedBook?.id === book.id
                      ? "tools-btn--primary"
                      : "tools-btn--secondary"
                  }`}
                >
                  {book.name || `Book #${book.id}`}
                </button>
              ))}
            </div>
          )}
        </div>

        {selectedBook && (
          <button
            className="tools-btn tools-btn--success tools-btn--sm"
            onClick={() => {
              setCustomerForm({ id: null, name: "", mobile_number: "" });
              setShowCustomerModal(true);
            }}
          >
            <FiUserPlus /> Add Customer
          </button>
        )}
      </div>

      {/* Book Summary Metrics */}
      {selectedBook && (
        <div className="tools-stats-grid">
          <div className="tools-stat-card">
            <div className="tools-stat-card__icon tools-stat-card__icon--green">
              <FiArrowDownLeft />
            </div>
            <div>
              <div className="tools-stat-card__label">You'll Get (Receivable)</div>
              <div className="tools-stat-card__value text-success">
                ₹{Number(totalYouWillGet).toLocaleString("en-IN")}
              </div>
            </div>
          </div>

          <div className="tools-stat-card">
            <div className="tools-stat-card__icon tools-stat-card__icon--red">
              <FiArrowUpRight />
            </div>
            <div>
              <div className="tools-stat-card__label">You'll Give (Payable)</div>
              <div className="tools-stat-card__value text-danger">
                ₹{Number(totalYouWillGive).toLocaleString("en-IN")}
              </div>
            </div>
          </div>

          <div className="tools-stat-card">
            <div className="tools-stat-card__icon tools-stat-card__icon--blue">
              <FiDollarSign />
            </div>
            <div>
              <div className="tools-stat-card__label">Net Balance</div>
              <div
                className={`tools-stat-card__value ${
                  netBalance >= 0 ? "text-success" : "text-danger"
                }`}
              >
                ₹{Number(Math.abs(netBalance)).toLocaleString("en-IN")}{" "}
                <span style={{ fontSize: "0.8rem", fontWeight: "normal" }}>
                  {netBalance >= 0 ? "(Advance/Gain)" : "(Due to pay)"}
                </span>
              </div>
            </div>
          </div>

          <div className="tools-stat-card">
            <div className="tools-stat-card__icon tools-stat-card__icon--purple">
              <FiUser />
            </div>
            <div>
              <div className="tools-stat-card__label">Total Customers</div>
              <div className="tools-stat-card__value text-dark">{customers.length}</div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Search & Filter */}
      {selectedBook && (
        <div className="tools-action-bar">
          <div className="tools-search-box">
            <FiSearch />
            <input
              type="text"
              placeholder="Search customers by name or mobile number..."
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
            />
          </div>
          <span className="text-muted small">
            Showing {filteredCustomers.length} of {customers.length} customer(s)
          </span>
        </div>
      )}

      {/* Customers Table / List */}
      {!selectedBook ? (
        <div className="tools-empty-state">
          <div className="tools-empty-state__icon">
            <FiBookOpen />
          </div>
          <h4>No Khatabook Selected</h4>
          <p>Create or select a Khatabook from above to manage customer ledgers.</p>
          <button
            className="tools-btn tools-btn--primary"
            onClick={() => setShowCreateBookModal(true)}
          >
            <FiPlus /> Create Khatabook
          </button>
        </div>
      ) : loadingCustomers ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Loading customers...</p>
        </div>
      ) : filteredCustomers.length === 0 ? (
        <div className="tools-empty-state">
          <div className="tools-empty-state__icon">
            <FiUser />
          </div>
          <h4>No Customers in this Khatabook</h4>
          <p>Add customers to start recording credit and payment transactions.</p>
          <button
            className="tools-btn tools-btn--primary"
            onClick={() => {
              setCustomerForm({ id: null, name: "", mobile_number: "" });
              setShowCustomerModal(true);
            }}
          >
            <FiUserPlus /> Add Customer
          </button>
        </div>
      ) : (
        <div className="tools-table-card">
          <div className="tools-table-responsive">
            <table className="tools-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Mobile Number</th>
                  <th>Balance Status</th>
                  <th>Amount</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((cust) => {
                  const balance = Number(cust.balance || cust.net_amount || 0);
                  const isYouWillGet = balance >= 0;
                  return (
                    <tr
                      key={cust.id}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleOpenCustomer(cust)}
                    >
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: "50%",
                              background: "#eff6ff",
                              color: "#2563eb",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "bold",
                            }}
                          >
                            {cust.name ? cust.name.charAt(0).toUpperCase() : "C"}
                          </div>
                          <div>
                            <div className="fw-bold text-dark">{cust.name}</div>
                            <div className="text-muted small">
                              ID: #{cust.id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="text-secondary d-flex align-items-center gap-1">
                          <FiPhone size={13} /> {cust.mobile_number || "—"}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`tools-badge ${
                            isYouWillGet ? "tools-badge--got" : "tools-badge--gave"
                          }`}
                        >
                          {isYouWillGet ? "You'll Get" : "You'll Give"}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`fw-bold ${
                            isYouWillGet ? "text-success" : "text-danger"
                          }`}
                        >
                          ₹{Math.abs(balance).toLocaleString("en-IN")}
                        </span>
                      </td>
                      <td style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                        <div className="d-flex align-items-center justify-content-end gap-1">
                          <button
                            className="tools-btn tools-btn--sm tools-btn--secondary"
                            onClick={() => handleOpenCustomer(cust)}
                          >
                            View Passbook
                          </button>
                          <button
                            className="tools-btn--icon"
                            title="Edit Customer"
                            onClick={() => {
                              setCustomerForm({
                                id: cust.id,
                                name: cust.name,
                                mobile_number: cust.mobile_number,
                              });
                              setShowCustomerModal(true);
                            }}
                          >
                            <FiEdit2 size={14} />
                          </button>
                          <button
                            className="tools-btn--icon tools-btn--icon-danger"
                            title="Delete Customer"
                            onClick={(e) => handleDeleteCustomer(cust.id, e)}
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Customer Passbook Drawer / Side Panel */}
      {selectedCustomer && (
        <div className="tools-drawer-overlay" onClick={() => setSelectedCustomer(null)}>
          <div className="tools-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="tools-drawer__header">
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    background: "#eff6ff",
                    color: "#2563eb",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                  }}
                >
                  {selectedCustomer.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h4 className="fw-bold mb-0 text-dark">{selectedCustomer.name}</h4>
                  <div className="text-muted small d-flex align-items-center gap-1">
                    <FiPhone size={13} /> {selectedCustomer.mobile_number}
                  </div>
                </div>
              </div>
              <button
                className="tools-btn--icon"
                onClick={() => setSelectedCustomer(null)}
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="tools-drawer__body">
              {/* Customer Summary Cards */}
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <div
                    style={{
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      borderRadius: 10,
                      padding: "10px 14px",
                    }}
                  >
                    <div className="text-muted small fw-semibold">You Gave (Got Goods/Money)</div>
                    <div className="fw-bold text-danger fs-5">
                      ₹
                      {Number(
                        customerSummary?.total_give ||
                          customerSummary?.total_gave ||
                          0
                      ).toLocaleString("en-IN")}
                    </div>
                  </div>
                </div>
                <div className="col-6">
                  <div
                    style={{
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      borderRadius: 10,
                      padding: "10px 14px",
                    }}
                  >
                    <div className="text-muted small fw-semibold">You Got (Received Money)</div>
                    <div className="fw-bold text-success fs-5">
                      ₹
                      {Number(
                        customerSummary?.total_get ||
                          customerSummary?.total_got ||
                          0
                      ).toLocaleString("en-IN")}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons for Customer Transaction */}
              <div className="d-flex gap-2 mb-4">
                <button
                  className="tools-btn tools-btn--danger flex-grow-1"
                  onClick={() => {
                    setTxForm({
                      id: null,
                      amount: "",
                      transaction_type: "GAVE",
                      description: "",
                    });
                    setShowTxModal(true);
                  }}
                >
                  <FiArrowUpRight /> You Gave ₹ (Debit)
                </button>
                <button
                  className="tools-btn tools-btn--success flex-grow-1"
                  onClick={() => {
                    setTxForm({
                      id: null,
                      amount: "",
                      transaction_type: "GOT",
                      description: "",
                    });
                    setShowTxModal(true);
                  }}
                >
                  <FiArrowDownLeft /> You Got ₹ (Credit)
                </button>
              </div>

              {/* Transactions History Header */}
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="fw-bold text-dark mb-0">Transaction History</h6>
                <button
                  className="tools-btn--icon"
                  onClick={() => handleOpenCustomer(selectedCustomer)}
                  title="Refresh Transactions"
                >
                  <FiRefreshCw size={14} className={loadingTransactions ? "spin" : ""} />
                </button>
              </div>

              {/* Transactions List */}
              {loadingTransactions ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" variant="primary" />
                  <p className="small text-muted mt-2">Loading transactions...</p>
                </div>
              ) : transactions.length === 0 ? (
                <div className="tools-empty-state py-4">
                  <p className="mb-0">No transactions recorded yet.</p>
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {transactions.map((tx) => {
                    const isGave =
                      String(tx.transaction_type || tx.type).toUpperCase() ===
                      "GAVE" ||
                      String(tx.transaction_type || tx.type).toUpperCase() ===
                      "DEBIT";
                    return (
                      <div
                        key={tx.id}
                        style={{
                          border: "1px solid #e2e8f0",
                          borderRadius: 10,
                          padding: "12px 14px",
                          background: "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 10,
                        }}
                      >
                        <div>
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <span
                              className={`tools-badge ${
                                isGave ? "tools-badge--gave" : "tools-badge--got"
                              }`}
                            >
                              {isGave ? "You Gave" : "You Got"}
                            </span>
                            <span className="text-muted small">
                              {tx.created_on
                                ? new Date(tx.created_on).toLocaleDateString("en-IN", {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : tx.date || "Recent"}
                            </span>
                          </div>
                          {tx.description && (
                            <div className="text-secondary small">{tx.description}</div>
                          )}
                        </div>

                        <div className="d-flex align-items-center gap-2">
                          <span
                            className={`fw-bold fs-6 ${
                              isGave ? "text-danger" : "text-success"
                            }`}
                          >
                            ₹{Number(tx.amount || 0).toLocaleString("en-IN")}
                          </span>
                          <button
                            className="tools-btn--icon"
                            title="Edit"
                            onClick={() => {
                              setTxForm({
                                id: tx.id,
                                amount: tx.amount,
                                transaction_type: tx.transaction_type || (isGave ? "GAVE" : "GOT"),
                                description: tx.description || "",
                              });
                              setShowTxModal(true);
                            }}
                          >
                            <FiEdit2 size={13} />
                          </button>
                          <button
                            className="tools-btn--icon tools-btn--icon-danger"
                            title="Delete"
                            onClick={() => handleDeleteTransaction(tx.id)}
                          >
                            <FiTrash2 size={13} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Create Khatabook */}
      <Modal show={showCreateBookModal} onHide={() => setShowCreateBookModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5 fw-bold">Create New Khatabook</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateBook}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Khatabook Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. HOME, SHOP, SITE A"
                value={newBookName}
                onChange={(e) => setNewBookName(e.target.value)}
                required
                autoFocus
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={() => setShowCreateBookModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submittingBook}>
              {submittingBook ? "Creating..." : "Create Khatabook"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal: Add/Edit Customer */}
      <Modal show={showCustomerModal} onHide={() => setShowCustomerModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5 fw-bold">
            {customerForm.id ? "Edit Customer" : "Add Customer to Khatabook"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveCustomer}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Customer Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. John Doe"
                value={customerForm.name}
                onChange={(e) =>
                  setCustomerForm({ ...customerForm, name: e.target.value })
                }
                required
                autoFocus
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Mobile Number *</Form.Label>
              <Form.Control
                type="tel"
                placeholder="e.g. 9876543210"
                value={customerForm.mobile_number}
                onChange={(e) =>
                  setCustomerForm({
                    ...customerForm,
                    mobile_number: e.target.value,
                  })
                }
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={() => setShowCustomerModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submittingCustomer}>
              {submittingCustomer ? "Saving..." : customerForm.id ? "Update" : "Add Customer"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal: Add/Edit Transaction */}
      <Modal show={showTxModal} onHide={() => setShowTxModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5 fw-bold">
            {txForm.id ? "Edit Transaction" : "Record Transaction"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveTransaction}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Transaction Type *</Form.Label>
              <div className="d-flex gap-2">
                <Button
                  variant={txForm.transaction_type === "GAVE" ? "danger" : "outline-danger"}
                  className="flex-grow-1"
                  type="button"
                  onClick={() => setTxForm({ ...txForm, transaction_type: "GAVE" })}
                >
                  <FiArrowUpRight /> You Gave (Debit)
                </Button>
                <Button
                  variant={txForm.transaction_type === "GOT" ? "success" : "outline-success"}
                  className="flex-grow-1"
                  type="button"
                  onClick={() => setTxForm({ ...txForm, transaction_type: "GOT" })}
                >
                  <FiArrowDownLeft /> You Got (Credit)
                </Button>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Amount (₹) *</Form.Label>
              <Form.Control
                type="number"
                placeholder="e.g. 5000"
                value={txForm.amount}
                onChange={(e) => setTxForm({ ...txForm, amount: e.target.value })}
                required
                min="1"
                autoFocus
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Description / Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="e.g. Materials purchased, Partial payment, etc."
                value={txForm.description}
                onChange={(e) =>
                  setTxForm({ ...txForm, description: e.target.value })
                }
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={() => setShowTxModal(false)}>
              Cancel
            </Button>
            <Button
              variant={txForm.transaction_type === "GAVE" ? "danger" : "success"}
              type="submit"
              disabled={submittingTx}
            >
              {submittingTx ? "Saving..." : txForm.id ? "Update Entry" : "Save Entry"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default KhatabookTool;
